import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { adminService } from '../services/adminService';
import AdminStats from '../components/Admin/AdminStats';
import AdminFilters from '../components/Admin/AdminFilters';
import AdminBookingList from '../components/Admin/AdminBookingList';
import AdminQuickActions from '../components/Admin/AdminQuickActions';
import AdminAnalytics from '../components/Admin/AdminAnalytics';
import AdminModals from '../components/Admin/AdminModals';
import UserManagement from '../components/Admin/UserManagement';
import AdminReconciliation from './AdminReconciliation';
import AdminEvents from '../components/Admin/AdminEvents';
import AdminRaffle from '../components/Admin/AdminRaffle';
import AdminDashboard from './AdminDashboard';
import AdminReviewPanel from '../components/Admin/AdminReviewPanel';
import AdminGallery from '../components/Admin/AdminGallery';
import './AdminPage.css';

// ============================================
// ROLE PERMISSIONS CONFIGURATION
// ============================================
const ROLES_CONFIG = {
  admin: {
    level: 100,
    name: 'Super Administrator',
    category: 'Executive',
    can: ['everything', 'manage_all_users', 'assign_any_role', 'delete_any_user', 'view_all_data', 'edit_any_booking', 'delete_any_booking', 'manage_system_settings', 'view_system_logs', 'manage_backups', 'approve_bookings', 'manage_members', 'manage_sections', 'manage_events', 'manage_raffle', 'view_analytics', 'manage_blackout', 'manage_pricing', 'manage_announcements', 'approve_associates', 'send_communications', 'manage_finances', 'manage_gallery']
  },
  president: {
    level: 95,
    name: 'President',
    category: 'Executive',
    can: ['approve_bookings', 'manage_members', 'manage_staff', 'manage_sections', 'manage_events', 'manage_raffle', 'view_analytics', 'manage_settings', 'delegate_tasks', 'view_all_data', 'edit_any_booking', 'manage_announcements', 'approve_associates', 'assign_committee_roles', 'view_financial_summary', 'manage_blackout', 'manage_pricing', 'send_communications', 'manage_gallery']
  },
  vicePresident: {
    level: 90,
    name: 'Vice President',
    category: 'Executive',
    can: ['approve_bookings', 'manage_members', 'manage_sections', 'manage_events', 'manage_raffle', 'view_analytics', 'view_all_data', 'edit_any_booking', 'manage_announcements', 'view_financial_summary', 'act_as_president_when_delegated']
  },
  secretary: {
    level: 85,
    name: 'Secretary',
    category: 'Executive',
    can: ['view_bookings', 'manage_members', 'manage_sections', 'manage_events', 'view_analytics', 'view_all_data', 'manage_announcements', 'send_communications', 'manage_documents', 'record_minutes', 'manage_correspondence']
  },
  treasurer: {
    level: 85,
    name: 'Treasurer',
    category: 'Executive',
    can: ['view_bookings', 'view_financial_data', 'view_analytics', 'view_all_data', 'manage_pricing', 'generate_reports', 'process_payments', 'view_financial_reports', 'manage_budget']
  },
  sportsMember: {
    level: 60,
    name: 'Sports Member',
    category: 'Committee',
    department: 'Sports',
    can: ['manage_sports_sections', 'view_sports_bookings', 'manage_sports_events', 'view_sports_analytics', 'manage_sports_facilities', 'coordinate_tournaments', 'view_team_members', 'submit_sports_reports']
  },
  culturalMember: {
    level: 60,
    name: 'Cultural Member',
    category: 'Committee',
    department: 'Cultural',
    can: ['manage_cultural_sections', 'view_cultural_bookings', 'manage_cultural_events', 'view_cultural_analytics', 'organize_cultural_programs', 'manage_art_groups', 'coordinate_festivals', 'submit_cultural_reports']
  },
  technicalMember: {
    level: 60,
    name: 'Technical Member',
    category: 'Committee',
    department: 'Technical',
    can: ['manage_technical_sections', 'view_technical_bookings', 'manage_technical_events', 'view_facilities_maintenance', 'manage_equipment', 'submit_technical_reports', 'oversee_maintenance', 'manage_inventory']
  },
  socialMember: {
    level: 60,
    name: 'Social Member',
    category: 'Committee',
    department: 'Social',
    can: ['manage_social_sections', 'view_social_bookings', 'manage_social_events', 'view_social_analytics', 'organize_social_gatherings', 'manage_membership_drives', 'coordinate_networking_events', 'submit_social_reports']
  },
  generalMember: {
    level: 50,
    name: 'General Member',
    category: 'Committee',
    can: ['view_all_sections', 'view_all_events', 'submit_reports', 'participate_in_discussions', 'view_committee_updates', 'submit_suggestions']
  },
  clubManager: {
    level: 75,
    name: 'Club Manager',
    category: 'Staff',
    can: ['view_all_bookings', 'manage_facilities', 'manage_maintenance', 'view_all_data', 'manage_blackout', 'approve_bookings', 'manage_staff_schedule', 'handle_complaints', 'manage_daily_operations', 'view_operational_reports']
  },
  clubAdministrator: {
    level: 70,
    name: 'Club Administrator',
    category: 'Staff',
    can: ['view_all_bookings', 'manage_member_records', 'manage_documents', 'view_all_data', 'process_payments', 'handle_complaints', 'manage_correspondence', 'update_member_profiles', 'generate_member_reports']
  },
  member: {
    level: 10,
    name: 'Regular Member',
    category: 'Regular',
    can: ['view_own_profile', 'update_own_profile', 'make_bookings', 'view_own_bookings', 'join_sections', 'view_sections', 'register_for_events', 'view_events', 'buy_raffle_tickets', 'submit_feedback', 'view_announcements']
  }
};

const hasPermission = (userRole, permission) => {
  const roleConfig = ROLES_CONFIG[userRole];
  if (!roleConfig) return false;
  if (roleConfig.can.includes('everything')) return true;
  return roleConfig.can.includes(permission);
};

const AdminPage = ({
  user = null,
  isAdmin = false,
  isPresident = false,
  isVicePresident = false,
  isSecretary = false,
  isTreasurer = false,
  isSportsMember = false,
  isCulturalMember = false,
  isTechnicalMember = false,
  isSocialMember = false,
  isGeneralMember = false,
  isClubManager = false,
  isClubAdministrator = false,
  presidentDelegation = false,
  addNotification = () => {},
  addAuditLog = () => {}
}) => {
  // Tab state
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Section sub-tab state
  const [sectionSubTab, setSectionSubTab] = useState('pending');
  
  // Management sub-tab state
  const [managementSubTab, setManagementSubTab] = useState('sections');
  
  // Data states
  const [allBookings, setAllBookings] = useState([]);
  const [pendingBookings, setPendingBookings] = useState([]);
  const [pendingMembers, setPendingMembers] = useState([]);
  const [pendingSections, setPendingSections] = useState([]);
  const [approvedSections, setApprovedSections] = useState([]);
  const [rejectedSections, setRejectedSections] = useState([]);
  const [allSections, setAllSections] = useState([]);
  const [waitlistBookings, setWaitlistBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [stats, setStats] = useState({
    total: 0, pending: 0, approved: 0, rejected: 0, cancelled: 0,
    waitlist: 0, today: 0, thisWeek: 0, thisMonth: 0,
    religious: 0, political: 0, sporting: 0,
    equipment: 0, facilities: 0, memberships: 0, sections: 0, locationChanged: 0
  });
  const [analytics, setAnalytics] = useState({
    total: 0, byType: { facility: 0, equipment: 0, membership: 0, section: 0 },
    byStatus: { pending: 0, approved: 0, rejected: 0, cancelled: 0, waitlist: 0, locationChanged: 0 },
    popularFacilities: [], communications: { sms: 0, whatsapp: 0, email: 0 }, byDay: {}
  });
  const [auditLog, setAuditLog] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [maintenanceSchedule, setMaintenanceSchedule] = useState([]);
  const [blackoutDates, setBlackoutDates] = useState([]);
  const [pricingRules, setPricingRules] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [announcements, setAnnouncements] = useState([]);
  const [delegatedUsers, setDelegatedUsers] = useState([]);
  const [communicationHistory, setCommunicationHistory] = useState([]);
  const [locationChangeHistory, setLocationChangeHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  // Filter states
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterFacility, setFilterFacility] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [sortBy, setSortBy] = useState('date_desc');
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analyticsPeriod, setAnalyticsPeriod] = useState('month');
  
  // Modal states
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showDelegateModal, setShowDelegateModal] = useState(false);
  const [showBulkActionModal, setShowBulkActionModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showAuditLogModal, setShowAuditLogModal] = useState(false);
  const [showChangeLocationModal, setShowChangeLocationModal] = useState(false);
  const [showCommunicationModal, setShowCommunicationModal] = useState(false);
  const [showSMSModal, setShowSMSModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [showBlackoutModal, setShowBlackoutModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [showViewDetailsModal, setShowViewDetailsModal] = useState(false);
  
  // Selected items
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  
  // Communication states
  const [smsMessage, setSmsMessage] = useState('');
  const [whatsAppMessage, setWhatsAppMessage] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  
  // Location change states
  const [newLocation, setNewLocation] = useState('');
  const [locationChangeReason, setLocationChangeReason] = useState('');
  const [availableLocations, setAvailableLocations] = useState([
    'Abuja Boulevard', 'Banquet Hall', 'Market Square', 'Marquee Tent',
    'Clubhouse West-Bar', 'DBN Bar', 'Golf Course Bar', 'Hippo Bar',
    'Clubhouse Alcove', 'West Bar Meeting Room', 'Sports Hall Complex',
    'Tennis Courts', 'Swimming Pool', 'Football Pitch', 'Squash Courts',
    'Golf Course', 'Kids Play Area', 'Wellness Center', 'Finima Nature Park',
    'Main Complex', 'West Wing', 'East Wing', 'Central Plaza',
    'Sports Complex', 'Garden Area', 'Clubhouse', 'Poolside'
  ]);
  
  // Maintenance states
  const [maintenanceItem, setMaintenanceItem] = useState({
    facility: '', startDate: '', endDate: '', reason: '', status: 'scheduled'
  });
  
  // Blackout states
  const [blackoutItem, setBlackoutItem] = useState({ facility: '', date: '', reason: '' });
  
  // Pricing states
  const [pricingItem, setPricingItem] = useState({ facility: '', dayType: 'weekday', timeSlot: '', price: 0 });
  
  // Permissions states
  const [selectedRole, setSelectedRole] = useState('');
  const [rolePermissions, setRolePermissions] = useState([]);
  
  // Announcement states
  const [announcementItem, setAnnouncementItem] = useState({ title: '', message: '', target: 'all', expiryDate: '' });
  
  // ============================================
  // MANAGEMENT TAB STATES
  // ============================================
  const [facilitiesList, setFacilitiesList] = useState([]);
  const [equipmentList, setEquipmentList] = useState([]);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [resourceFormData, setResourceFormData] = useState({});
  const [savingResource, setSavingResource] = useState(false);
  
  const [sectionVersion, setSectionVersion] = useState(0);
  const [bookingsCurrentPage, setBookingsCurrentPage] = useState(1);
  const [bookingsItemsPerPage] = useState(6);
  
  // Determine role-based access
  const currentUserRole = user?.account_type || 'member';
  const canApprove = hasPermission(currentUserRole, 'approve_bookings') || isPresident || (isVicePresident && presidentDelegation) || isAdmin;
  const canManageUsers = hasPermission(currentUserRole, 'manage_all_users') || isAdmin || isPresident;
  const canAssignRoles = hasPermission(currentUserRole, 'assign_any_role') || isAdmin || (isPresident && hasPermission(currentUserRole, 'assign_committee_roles'));
  const canViewAnalytics = hasPermission(currentUserRole, 'view_analytics') || isAdmin || isPresident || isVicePresident || isSecretary || isTreasurer;
  const canManageSections = hasPermission(currentUserRole, 'manage_sections') || isAdmin || isPresident || isVicePresident || isSecretary;
  const canManageEvents = hasPermission(currentUserRole, 'manage_events') || isAdmin || isPresident || isVicePresident || isSecretary;
  const canManageRaffle = hasPermission(currentUserRole, 'manage_raffle') || isAdmin || isPresident || isVicePresident;
  const canManageBlackout = hasPermission(currentUserRole, 'manage_blackout') || isAdmin || isPresident || isClubManager;
  const canManagePricing = hasPermission(currentUserRole, 'manage_pricing') || isAdmin || isPresident || isTreasurer;
  const canManageAnnouncements = hasPermission(currentUserRole, 'manage_announcements') || isAdmin || isPresident || isVicePresident || isSecretary;
  const canApproveAssociates = hasPermission(currentUserRole, 'approve_associates') || isAdmin || isPresident;
  const canManageGallery = hasPermission(currentUserRole, 'manage_gallery') || isAdmin || isPresident;
  
  const isMounted = useRef(true);

  // ============================================
  // LOAD FUNCTIONS - FIXED
  // ============================================
  
  const loadAllUsers = async () => {
    console.log('🟢 loadAllUsers - START');
    if (!isMounted.current) return;
    try {
      const response = await adminService.getAllUsers();
      if (response.success && response.users && isMounted.current) {
        setAllUsers(response.users);
        console.log('✅ Users loaded:', response.users.length);
      } else if (isMounted.current) {
        setAllUsers([]);
      }
    } catch (error) {
      console.error('❌ Error loading users:', error);
      if (isMounted.current) setAllUsers([]);
    }
  };

  const loadAllSections = async () => {
    if (!isMounted.current) return;
    try {
      const response = await adminService.getAllSectionsWithCounts();
      if (response.success && response.sections && isMounted.current) {
        const processed = response.sections.map(s => ({
          ...s,
          id: parseInt(s.id),
          approved_count: parseInt(s.approved_count) || 0,
          pending_count: parseInt(s.pending_count) || 0,
          capacity: parseInt(s.capacity) || 0,
          member_count: parseInt(s.approved_count) || 0,
          enrolled: parseInt(s.approved_count) || 0
        }));
        setAllSections(processed);
        setSectionVersion(prev => prev + 1);
        console.log('✅ Sections loaded:', processed.length);
      } else if (isMounted.current) {
        setAllSections([]);
      }
    } catch (error) {
      console.error('❌ Error loading sections:', error);
      if (isMounted.current) setAllSections([]);
    }
  };

  const loadAllSectionRequests = async () => {
    if (!isMounted.current) return;
    try {
      const pendingResponse = await adminService.getSectionRequests('pending');
      if (pendingResponse.success && pendingResponse.requests && isMounted.current) {
        const pending = pendingResponse.requests.filter(r => r.status?.toLowerCase() === 'pending');
        setPendingSections(pending);
      }
      
      const approvedResponse = await adminService.getSectionRequests('approved');
      if (approvedResponse.success && approvedResponse.requests && isMounted.current) {
        const approved = approvedResponse.requests.filter(r => r.status?.toLowerCase() === 'approved');
        setApprovedSections(approved);
      }
      
      const rejectedResponse = await adminService.getSectionRequests('rejected');
      if (rejectedResponse.success && rejectedResponse.requests && isMounted.current) {
        const rejected = rejectedResponse.requests.filter(r => r.status?.toLowerCase() === 'rejected');
        setRejectedSections(rejected);
      }
      console.log('✅ Section requests loaded - Pending:', pendingSections.length);
    } catch (error) {
      console.error('❌ Error loading section requests:', error);
    }
  };
  
  const loadFacilities = async () => {
    if (!isMounted.current) return;
    try {
      const response = await adminService.listFacilities();
      if (response.success && isMounted.current) {
        const facilitiesData = response.data || response.facilities || [];
        setFacilitiesList(facilitiesData);
        console.log('✅ Facilities loaded:', facilitiesData.length);
      } else if (isMounted.current) {
        setFacilitiesList([]);
      }
    } catch (error) {
      console.error('❌ Error loading facilities:', error);
      if (isMounted.current) setFacilitiesList([]);
    }
  };
  
  const loadEquipment = async () => {
    if (!isMounted.current) return;
    try {
      const response = await adminService.listEquipment();
      if (response.success && isMounted.current) {
        const equipmentData = response.data || response.equipment || [];
        setEquipmentList(equipmentData);
        console.log('✅ Equipment loaded:', equipmentData.length);
      } else if (isMounted.current) {
        setEquipmentList([]);
      }
    } catch (error) {
      console.error('❌ Error loading equipment:', error);
      if (isMounted.current) setEquipmentList([]);
    }
  };
  
  const loadBookings = async () => {
    if (!isMounted.current) return;
    try {
      const bookingsResponse = await adminService.getAllBookings();
      if (bookingsResponse.success && bookingsResponse.bookings && isMounted.current) {
        const bookings = bookingsResponse.bookings || [];
        
        const processedBookings = bookings.map(booking => ({
          ...booking,
          dependent_approval_file: booking.dependent_approval_file || null,
          member_membership_type: booking.member_membership_type || booking.membership_type,
          member_relationship_type: booking.member_relationship_type || booking.relationship_type,
          member_requires_approval: booking.member_requires_approval || 
            (booking.member_membership_type === 'dependent' && booking.member_relationship_type !== 'spouse'),
          equipment_items: Array.isArray(booking.equipment_items) ? booking.equipment_items : 
            (typeof booking.equipment_items === 'string' && booking.equipment_items ? 
              (() => { try { return JSON.parse(booking.equipment_items); } catch(e) { return []; } })() : [])
        }));
        
        console.log('📊 Loaded bookings:', processedBookings.length);
        
        setAllBookings(processedBookings);
        setPendingBookings(processedBookings.filter(b => b.status === 'pending'));
        setWaitlistBookings(processedBookings.filter(b => b.status === 'waitlist'));
        setFilteredBookings(processedBookings);
      } else if (isMounted.current) {
        setAllBookings([]);
        setPendingBookings([]);
        setWaitlistBookings([]);
        setFilteredBookings([]);
      }
    } catch (error) {
      console.error('❌ Error loading bookings:', error);
      if (isMounted.current) {
        setAllBookings([]);
        setPendingBookings([]);
        setWaitlistBookings([]);
        setFilteredBookings([]);
      }
    }
  };
  
  const loadPendingMembers = async () => {
    if (!isMounted.current) return;
    try {
      const membersResponse = await adminService.getPendingMembers();
      if (membersResponse.success && membersResponse.users && isMounted.current) {
        setPendingMembers(membersResponse.users || []);
        console.log('✅ Pending members loaded:', membersResponse.users.length);
      } else if (isMounted.current) {
        setPendingMembers([]);
      }
    } catch (error) {
      console.error('❌ Error loading pending members:', error);
      if (isMounted.current) setPendingMembers([]);
    }
  };
  
  const loadAuditLogs = async () => {
    if (!isMounted.current) return;
    try {
      const auditResponse = await adminService.getAuditLogs(100);
      if (auditResponse.success && auditResponse.logs && isMounted.current) {
        setAuditLog(auditResponse.logs || []);
      }
    } catch (error) {
      console.error('❌ Error loading audit logs:', error);
    }
  };
  
  const loadFeedbacks = async () => {
    if (!isMounted.current) return;
    try {
      const feedbackResponse = await adminService.getFeedback();
      if (feedbackResponse.success && feedbackResponse.feedbacks && isMounted.current) {
        setFeedbacks(feedbackResponse.feedbacks || []);
      }
    } catch (error) {
      console.error('❌ Error loading feedbacks:', error);
    }
  };
  
  const refreshSectionsData = async () => {
    await Promise.all([loadAllSections(), loadAllSectionRequests()]);
    if (isMounted.current) setSectionVersion(prev => prev + 1);
  };
  
  const loadAllData = async () => {
    if (!isMounted.current) return;
    
    console.log('🟢 Starting loadAllData...');
    setLoading(true);
    
    try {
      // Load data in parallel for better performance
      await Promise.all([
        loadBookings(),
        loadAllSections(),
        loadAllSectionRequests(),
        loadFacilities(),
        loadEquipment(),
        loadAllUsers(),
        loadPendingMembers(),
        loadAuditLogs(),
        loadFeedbacks()
      ]);
      
      console.log('✅ All data loaded successfully');
      if (isMounted.current) {
        setInitialLoadComplete(true);
      }
      
    } catch (error) {
      console.error('❌ Error loading admin data:', error);
      if (isMounted.current) {
        addNotification('Failed to load admin data. Please refresh the page.', 'error');
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  // ============================================
  // RESOURCE CRUD HANDLERS
  // ============================================
  
  const openResourceModal = (type, resource = null) => {
    setEditingResource(resource);
    if (resource) {
      setResourceFormData({ ...resource });
    } else {
      if (type === 'section') {
        setResourceFormData({ name: '', category: '', instructor: '', capacity: 0, icon: 'fa-users' });
      } else if (type === 'facility') {
        setResourceFormData({ name: '', category: '', description: '', location: '', capacity: '', maintenance_status: 'operational', icon: 'fa-building' });
      } else if (type === 'equipment') {
        setResourceFormData({ id: '', name: '', category: '', available: 0, sporting_only: 0, icon: 'fa-box' });
      }
    }
    setShowResourceModal(true);
  };
  
  const handleSaveResource = async () => {
    setSavingResource(true);
    try {
      if (editingResource) {
        if (editingResource.name && editingResource.category && editingResource.instructor !== undefined) {
          await adminService.updateSection(editingResource.id, resourceFormData);
        } else if (editingResource.location !== undefined) {
          await adminService.updateFacility(editingResource.id, resourceFormData);
        } else if (editingResource.available !== undefined) {
          await adminService.updateEquipment(editingResource.id, resourceFormData);
        }
        addNotification('Resource updated successfully', 'success');
      } else {
        if (resourceFormData.name && resourceFormData.category && resourceFormData.instructor !== undefined) {
          await adminService.createSection(resourceFormData);
        } else if (resourceFormData.location !== undefined) {
          await adminService.createFacility(resourceFormData);
        } else {
          await adminService.createEquipment(resourceFormData);
        }
        addNotification('Resource created successfully', 'success');
      }
      setShowResourceModal(false);
      // Refresh all data
      await Promise.all([loadAllSections(), loadFacilities(), loadEquipment()]);
    } catch (error) {
      console.error('Save resource error:', error);
      addNotification('Failed to save resource', 'error');
    } finally {
      setSavingResource(false);
    }
  };
  
  const deleteResource = async (type, id, name) => {
    if (!window.confirm(`Delete "${name}"? This action cannot be undone.`)) return;
    try {
      if (type === 'section') {
        await adminService.deleteSection(id);
      } else if (type === 'facility') {
        await adminService.deleteFacility(id);
      } else {
        await adminService.deleteEquipment(id);
      }
      addNotification(`${name} deleted successfully`, 'success');
      await Promise.all([loadAllSections(), loadFacilities(), loadEquipment()]);
    } catch (error) {
      console.error('Delete resource error:', error);
      addNotification(`Failed to delete ${name}`, 'error');
    }
  };

  // ============================================
  // FILTER BOOKINGS
  // ============================================
  
  useEffect(() => {
    if (!allBookings.length) return;
    
    let filtered = [...allBookings];
    
    if (filterStatus !== 'all') {
      filtered = filtered.filter(booking => booking.status === filterStatus);
    }
    
    if (filterType !== 'all') {
      filtered = filtered.filter(booking => booking.booking_type === filterType);
    }
    
    if (filterFacility !== 'all') {
      filtered = filtered.filter(booking => 
        booking.facility_name === filterFacility || 
        booking.location === filterFacility
      );
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(booking => 
        booking.booking_number?.toLowerCase().includes(term) ||
        booking.member_full_name?.toLowerCase().includes(term) ||
        booking.member_first_name?.toLowerCase().includes(term) ||
        booking.member_last_name?.toLowerCase().includes(term) ||
        booking.member_email?.toLowerCase().includes(term) ||
        booking.email?.toLowerCase().includes(term) ||
        booking.facility_name?.toLowerCase().includes(term) ||
        booking.location?.toLowerCase().includes(term)
      );
    }
    
    if (dateRange.start) {
      const startDate = new Date(dateRange.start);
      startDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(booking => {
        if (!booking.booking_date) return false;
        const bookingDate = new Date(booking.booking_date);
        return bookingDate >= startDate;
      });
    }
    
    if (dateRange.end) {
      const endDate = new Date(dateRange.end);
      endDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(booking => {
        if (!booking.booking_date) return false;
        const bookingDate = new Date(booking.booking_date);
        return bookingDate <= endDate;
      });
    }
    
    switch (sortBy) {
      case 'date_asc':
        filtered.sort((a, b) => new Date(a.booking_date) - new Date(b.booking_date));
        break;
      case 'date_desc':
        filtered.sort((a, b) => new Date(b.booking_date) - new Date(a.booking_date));
        break;
      case 'name_asc':
        filtered.sort((a, b) => (a.member_full_name || '').localeCompare(b.member_full_name || ''));
        break;
      case 'name_desc':
        filtered.sort((a, b) => (b.member_full_name || '').localeCompare(a.member_full_name || ''));
        break;
      case 'status_asc':
        filtered.sort((a, b) => (a.status || '').localeCompare(b.status || ''));
        break;
      case 'status_desc':
        filtered.sort((a, b) => (b.status || '').localeCompare(a.status || ''));
        break;
      default:
        break;
    }
    
    setFilteredBookings(filtered);
    
  }, [allBookings, filterStatus, filterType, filterFacility, searchTerm, dateRange, sortBy]);
  
  // Update stats when bookings change
  useEffect(() => {
    if (allBookings.length) {
      const approved = allBookings.filter(b => b.status === 'approved').length;
      const pending = allBookings.filter(b => b.status === 'pending').length;
      const rejected = allBookings.filter(b => b.status === 'rejected').length;
      const cancelled = allBookings.filter(b => b.status === 'cancelled').length;
      const waitlist = allBookings.filter(b => b.status === 'waitlist').length;
      
      setStats(prev => ({
        ...prev,
        total: allBookings.length,
        pending,
        approved,
        rejected,
        cancelled,
        waitlist
      }));
    }
  }, [allBookings]);
  
  // Initial load
  useEffect(() => {
    isMounted.current = true;
    loadAllData();
    
    return () => {
      isMounted.current = false;
    };
  }, []);

  // ============================================
  // BOOKING APPROVAL FUNCTIONS
  // ============================================

  const handleApproveBooking = async (bookingId) => {
    if (!canApprove) {
      addNotification('You do not have permission to approve bookings', 'error');
      return;
    }
    try {
      const response = await adminService.updateBookingStatus(bookingId, 'approved');
      if (response.success) {
        addNotification('Booking approved successfully!', 'success');
        if (addAuditLog) addAuditLog('Booking Approved', { bookingId });
        await loadBookings();
      } else {
        addNotification(response.message || 'Failed to approve booking', 'error');
      }
    } catch (error) {
      console.error('Approve booking error:', error);
      addNotification('An error occurred', 'error');
    }
  };

  const openRejectionModal = (booking) => {
    setSelectedBooking(booking);
    setShowRejectionModal(true);
  };

  const handleRejectBooking = async (bookingId, reason) => {
    if (!canApprove) {
      addNotification('You do not have permission to reject bookings', 'error');
      return;
    }
    try {
      const response = await adminService.updateBookingStatus(bookingId, 'rejected', reason);
      if (response.success) {
        addNotification('Booking rejected successfully', 'success');
        if (addAuditLog) addAuditLog('Booking Rejected', { bookingId, reason });
        await loadBookings();
      } else {
        addNotification(response.message || 'Failed to reject booking', 'error');
      }
    } catch (error) {
      console.error('Reject booking error:', error);
      addNotification('An error occurred', 'error');
    }
  };

  const submitRejection = async () => {
    if (selectedBooking && rejectionReason) {
      await handleRejectBooking(selectedBooking.id, rejectionReason);
      setShowRejectionModal(false);
      setRejectionReason('');
      setSelectedBooking(null);
    } else {
      addNotification('Please provide a reason for rejection', 'warning');
    }
  };

  // ============================================
  // BULK BOOKING ACTIONS
  // ============================================

  const handleBulkApprove = async () => {
    if (!canApprove) {
      addNotification('You do not have permission to approve bookings', 'error');
      return;
    }
    if (selectedBookings.length === 0) {
      addNotification('Please select at least one booking', 'warning');
      return;
    }
    
    setLoading(true);
    try {
      let successCount = 0;
      for (const bookingId of selectedBookings) {
        const response = await adminService.updateBookingStatus(bookingId, 'approved');
        if (response.success) successCount++;
      }
      addNotification(`${successCount} bookings approved successfully`, 'success');
      await loadBookings();
      setSelectedBookings([]);
    } catch (error) {
      console.error('Bulk approve error:', error);
      addNotification('Failed to process bulk approve', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkReject = async () => {
    if (!canApprove) {
      addNotification('You do not have permission to reject bookings', 'error');
      return;
    }
    if (selectedBookings.length === 0) {
      addNotification('Please select at least one booking', 'warning');
      return;
    }
    
    const reason = prompt('Enter rejection reason for all selected bookings:');
    if (!reason) return;
    
    setLoading(true);
    try {
      let successCount = 0;
      for (const bookingId of selectedBookings) {
        const response = await adminService.updateBookingStatus(bookingId, 'rejected', reason);
        if (response.success) successCount++;
      }
      addNotification(`${successCount} bookings rejected successfully`, 'success');
      await loadBookings();
      setSelectedBookings([]);
    } catch (error) {
      console.error('Bulk reject error:', error);
      addNotification('Failed to process bulk reject', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // WAITLIST MANAGEMENT
  // ============================================

  const handleAddToWaitlist = async (bookingId) => {
    try {
      const response = await adminService.updateBookingStatus(bookingId, 'waitlist');
      if (response.success) {
        addNotification('Booking added to waitlist', 'success');
        await loadBookings();
      } else {
        addNotification(response.message || 'Failed to add to waitlist', 'error');
      }
    } catch (error) {
      console.error('Add to waitlist error:', error);
      addNotification('An error occurred', 'error');
    }
  };

  // ============================================
  // BOOKING DETAILS VIEW
  // ============================================

  const handleViewBookingDetails = (booking) => {
    setSelectedBooking(booking);
    setShowViewDetailsModal(true);
  };

  // ============================================
  // EXPORT FUNCTIONS
  // ============================================

  const exportBookingsToCSV = () => {
    if (filteredBookings.length === 0) {
      addNotification('No bookings to export', 'warning');
      return;
    }
    
    const headers = ['ID', 'Booking Number', 'Member', 'Facility', 'Date', 'Time', 'Status', 'Guests', 'Created At'];
    const csvData = filteredBookings.map(booking => [
      booking.id,
      booking.booking_number,
      booking.member_full_name || `${booking.member_first_name} ${booking.member_last_name}`,
      booking.facility_name || booking.location,
      booking.booking_date,
      `${booking.start_time} - ${booking.end_time}`,
      booking.status,
      booking.guests,
      new Date(booking.created_at).toLocaleString()
    ]);
    
    const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bookings_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    addNotification('Bookings exported successfully', 'success');
  };

  const generateBookingReport = () => {
    const reportData = {
      generated_at: new Date().toISOString(),
      total_bookings: allBookings.length,
      pending: pendingBookings.length,
      approved: allBookings.filter(b => b.status === 'approved').length,
      rejected: allBookings.filter(b => b.status === 'rejected').length,
      cancelled: allBookings.filter(b => b.status === 'cancelled').length,
      waitlist: waitlistBookings.length,
      bookings_by_type: {
        facility: allBookings.filter(b => b.booking_type === 'facility').length,
        equipment: allBookings.filter(b => b.booking_type === 'equipment').length,
        section: allBookings.filter(b => b.booking_type === 'section').length
      },
      recent_bookings: filteredBookings.slice(0, 10)
    };
    
    const reportBlob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(reportBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `booking_report_${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    addNotification('Report generated successfully', 'success');
  };

  const resetFilters = () => {
    setFilterStatus('all');
    setFilterType('all');
    setFilterFacility('all');
    setSearchTerm('');
    setDateRange({ start: '', end: '' });
    setSortBy('date_desc');
    addNotification('Filters reset', 'info');
  };

  // ============================================
  // SECTION REQUEST HANDLERS
  // ============================================

  const handleSectionRequestAction = async (requestId, status, reason = null) => {
    if (!canManageSections) {
      addNotification('You do not have permission to manage sections', 'error');
      return;
    }
    try {
      const response = await adminService.updateSectionRequest(requestId, status, reason);
      if (response.success) {
        addNotification(`Section request ${status} successfully! An email has been sent to the member.`, 'success');
        await refreshSectionsData();
        await loadAllSections();
      } else {
        addNotification(response.message || 'Failed to update section request', 'error');
      }
    } catch (error) {
      console.error('Section request action error:', error);
      addNotification('An error occurred', 'error');
    }
  };

  // ============================================
  // OTHER HANDLERS
  // ============================================

  const handleAdminAction = async (bookingId, status, reason = null) => {
    if (!canApprove && status !== 'cancelled') {
      addNotification('You do not have permission to perform this action', 'error');
      return;
    }
    try {
      const response = await adminService.updateBookingStatus(bookingId, status, reason);
      if (response.success) {
        addNotification(`Booking ${status} successfully`, 'success');
        if (addAuditLog) addAuditLog(`Booking ${status}`, { bookingId, status, reason });
        await loadBookings();
      } else {
        addNotification(response.message || 'Failed to update booking', 'error');
      }
    } catch (error) {
      console.error('Admin action error:', error);
      addNotification('An error occurred', 'error');
    }
  };

  const handleMemberAction = async (memberId, sectionId, status, reason = null) => {
    if (!canManageUsers) {
      addNotification('You do not have permission to manage members', 'error');
      return;
    }
    try {
      const response = await adminService.updateSectionRequest(memberId, status, reason);
      if (response.success) {
        addNotification(`Membership request ${status} successfully`, 'success');
        await loadPendingMembers();
        await loadAllSections();
      } else {
        addNotification(response.message || 'Failed to update membership', 'error');
      }
    } catch (error) {
      console.error('Member action error:', error);
      addNotification('An error occurred', 'error');
    }
  };

  const handleLocationChange = async (bookingId, newLocation, reason) => {
    try {
      const response = await adminService.updateBookingStatus(bookingId, 'location_changed', reason, newLocation);
      if (response.success) {
        addNotification(`Location changed to ${newLocation}`, 'success');
        if (addAuditLog) addAuditLog('Location Changed', { bookingId, newLocation, reason });
        await loadBookings();
      } else {
        addNotification(response.message || 'Failed to change location', 'error');
      }
    } catch (error) {
      console.error('Location change error:', error);
      addNotification('An error occurred', 'error');
    }
  };

  const handleLocationChangeSubmit = () => {
    if (selectedBooking && newLocation && locationChangeReason) {
      handleLocationChange(selectedBooking.id, newLocation, locationChangeReason);
      setShowChangeLocationModal(false);
      setNewLocation('');
      setLocationChangeReason('');
    } else {
      addNotification('Please select a location and provide a reason', 'warning');
    }
  };

  const handleBulkAction = async (action) => {
    if (!canApprove) {
      addNotification('You do not have permission to perform bulk actions', 'error');
      return;
    }
    if (selectedBookings.length === 0) {
      addNotification('Please select at least one booking', 'warning');
      return;
    }
    
    setLoading(true);
    try {
      for (const bookingId of selectedBookings) {
        await adminService.updateBookingStatus(bookingId, action);
      }
      addNotification(`${selectedBookings.length} bookings ${action}ed successfully`, 'success');
      await loadBookings();
      setSelectedBookings([]);
      setShowBulkActionModal(false);
    } catch (error) {
      console.error('Bulk action error:', error);
      addNotification('Failed to process bulk action', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkCommunication = async (type) => {
    const recipients = filteredBookings
      .filter(b => selectedBookings.includes(b.id))
      .map(b => ({
        phone: b.contact_phone,
        whatsapp: b.contact_whatsapp,
        email: b.contact_email,
        name: b.full_name
      }));
    
    try {
      const response = await adminService.sendBulkCommunication(type, recipients, 
        type === 'sms' ? smsMessage : type === 'whatsapp' ? whatsAppMessage : emailBody,
        emailSubject
      );
      
      if (response.success) {
        addNotification(`${recipients.length} messages sent successfully`, 'success');
        setShowSMSModal(false);
        setShowWhatsAppModal(false);
        setShowEmailModal(false);
        setSmsMessage('');
        setWhatsAppMessage('');
        setEmailSubject('');
        setEmailBody('');
        setSelectedBookings([]);
      } else {
        addNotification(response.message || 'Failed to send messages', 'error');
      }
    } catch (error) {
      console.error('Bulk communication error:', error);
      addNotification('Failed to send messages', 'error');
    }
  };

  const handleDelegate = (userEmail, permissions) => {
    if (!hasPermission(currentUserRole, 'delegate_tasks')) {
      addNotification('You do not have permission to delegate tasks', 'error');
      return;
    }
    setDelegatedUsers([...delegatedUsers, {
      id: Date.now(),
      delegatedTo: userEmail,
      permissions: permissions,
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
    }]);
    addNotification(`Delegated to ${userEmail}`, 'success');
    setShowDelegateModal(false);
  };

  const detectConflicts = () => {
    const conflictsList = [];
    const bookingsByDate = {};
    
    allBookings.forEach(booking => {
      if (booking.status === 'approved' && booking.booking_date) {
        const key = `${booking.facility_id}_${booking.booking_date}`;
        if (!bookingsByDate[key]) bookingsByDate[key] = [];
        bookingsByDate[key].push(booking);
      }
    });
    
    Object.values(bookingsByDate).forEach(dayBookings => {
      for (let i = 0; i < dayBookings.length; i++) {
        for (let j = i + 1; j < dayBookings.length; j++) {
          const b1 = dayBookings[i];
          const b2 = dayBookings[j];
          if (b1.start_time < b2.end_time && b2.start_time < b1.end_time) {
            conflictsList.push({ booking1: b1, booking2: b2, type: 'time_conflict', id: Date.now() + i + j });
          }
        }
      }
    });
    
    setConflicts(conflictsList);
    if (conflictsList.length > 0) {
      addNotification(`${conflictsList.length} conflicts detected`, 'warning');
      setShowConflictModal(true);
    } else {
      addNotification('No conflicts found', 'success');
    }
  };

  const resolveConflict = (conflictId, resolution) => {
    setConflicts(conflicts.filter(c => c.id !== conflictId));
    addNotification('Conflict resolved', 'success');
  };

  const respondToFeedback = async (feedbackId, response) => {
    try {
      const result = await adminService.respondToFeedback(feedbackId, response);
      if (result.success) {
        addNotification('Feedback response sent', 'success');
        await loadFeedbacks();
      }
    } catch (error) {
      console.error('Respond to feedback error:', error);
      addNotification('Failed to send response', 'error');
    }
  };

  const scheduleMaintenance = async () => {
    if (!hasPermission(currentUserRole, 'manage_maintenance') && !isClubManager && !isAdmin && !isTechnicalMember) {
      addNotification('You do not have permission to schedule maintenance', 'error');
      return;
    }
    try {
      const result = await adminService.scheduleMaintenance(maintenanceItem);
      if (result.success) {
        addNotification('Maintenance scheduled successfully', 'success');
        setMaintenanceSchedule([...maintenanceSchedule, { ...maintenanceItem, id: Date.now() }]);
        setShowMaintenanceModal(false);
        setMaintenanceItem({ facility: '', startDate: '', endDate: '', reason: '', status: 'scheduled' });
      }
    } catch (error) {
      console.error('Schedule maintenance error:', error);
      addNotification('Failed to schedule maintenance', 'error');
    }
  };

  const cancelMaintenance = async (maintenanceId) => {
    if (!hasPermission(currentUserRole, 'manage_maintenance') && !isClubManager && !isAdmin && !isTechnicalMember) {
      addNotification('You do not have permission to cancel maintenance', 'error');
      return;
    }
    try {
      const result = await adminService.cancelMaintenance(maintenanceId);
      if (result.success) {
        addNotification('Maintenance cancelled', 'success');
        setMaintenanceSchedule(maintenanceSchedule.filter(m => m.id !== maintenanceId));
      }
    } catch (error) {
      console.error('Cancel maintenance error:', error);
      addNotification('Failed to cancel maintenance', 'error');
    }
  };

  const addBlackoutDate = async () => {
    if (!canManageBlackout) {
      addNotification('You do not have permission to add blackout dates', 'error');
      return;
    }
    try {
      const result = await adminService.addBlackoutDate(blackoutItem);
      if (result.success) {
        addNotification('Blackout date added', 'success');
        setBlackoutDates([...blackoutDates, { ...blackoutItem, id: Date.now() }]);
        setShowBlackoutModal(false);
        setBlackoutItem({ facility: '', date: '', reason: '' });
      }
    } catch (error) {
      console.error('Add blackout date error:', error);
      addNotification('Failed to add blackout date', 'error');
    }
  };

  const removeBlackoutDate = async (blackoutId) => {
    if (!canManageBlackout) {
      addNotification('You do not have permission to remove blackout dates', 'error');
      return;
    }
    try {
      const result = await adminService.removeBlackoutDate(blackoutId);
      if (result.success) {
        addNotification('Blackout date removed', 'success');
        setBlackoutDates(blackoutDates.filter(b => b.id !== blackoutId));
      }
    } catch (error) {
      console.error('Remove blackout date error:', error);
      addNotification('Failed to remove blackout date', 'error');
    }
  };

  const addPricingRule = async () => {
    if (!canManagePricing) {
      addNotification('You do not have permission to add pricing rules', 'error');
      return;
    }
    try {
      const result = await adminService.addPricingRule(pricingItem);
      if (result.success) {
        addNotification('Pricing rule added', 'success');
        setPricingRules([...pricingRules, { ...pricingItem, id: Date.now() }]);
        setShowPricingModal(false);
        setPricingItem({ facility: '', dayType: 'weekday', timeSlot: '', price: 0 });
      }
    } catch (error) {
      console.error('Add pricing rule error:', error);
      addNotification('Failed to add pricing rule', 'error');
    }
  };

  const updatePricingRule = async (ruleId, updates) => {
    if (!canManagePricing) {
      addNotification('You do not have permission to update pricing rules', 'error');
      return;
    }
    try {
      const result = await adminService.updatePricingRule(ruleId, updates);
      if (result.success) {
        addNotification('Pricing rule updated', 'success');
        setPricingRules(pricingRules.map(r => r.id === ruleId ? { ...r, ...updates } : r));
      }
    } catch (error) {
      console.error('Update pricing rule error:', error);
      addNotification('Failed to update pricing rule', 'error');
    }
  };

  const deletePricingRule = async (ruleId) => {
    if (!canManagePricing) {
      addNotification('You do not have permission to delete pricing rules', 'error');
      return;
    }
    try {
      const result = await adminService.deletePricingRule(ruleId);
      if (result.success) {
        addNotification('Pricing rule deleted', 'success');
        setPricingRules(pricingRules.filter(r => r.id !== ruleId));
      }
    } catch (error) {
      console.error('Delete pricing rule error:', error);
      addNotification('Failed to delete pricing rule', 'error');
    }
  };

  const updateRolePermissions = async (roleId, permissions) => {
    if (!canAssignRoles) {
      addNotification('You do not have permission to update role permissions', 'error');
      return;
    }
    try {
      const result = await adminService.updateRolePermissions(roleId, permissions);
      if (result.success) {
        addNotification('Permissions updated', 'success');
        setPermissions({ ...permissions, [roleId]: permissions });
        setShowPermissionsModal(false);
      }
    } catch (error) {
      console.error('Update permissions error:', error);
      addNotification('Failed to update permissions', 'error');
    }
  };

  const createAnnouncement = async () => {
    if (!canManageAnnouncements) {
      addNotification('You do not have permission to create announcements', 'error');
      return;
    }
    try {
      const result = await adminService.createAnnouncement(announcementItem);
      if (result.success) {
        addNotification('Announcement created', 'success');
        setAnnouncements([...announcements, { ...announcementItem, id: Date.now(), createdAt: new Date().toISOString() }]);
        setShowAnnouncementModal(false);
        setAnnouncementItem({ title: '', message: '', target: 'all', expiryDate: '' });
      }
    } catch (error) {
      console.error('Create announcement error:', error);
      addNotification('Failed to create announcement', 'error');
    }
  };

  const deleteAnnouncement = async (announcementId) => {
    if (!canManageAnnouncements) {
      addNotification('You do not have permission to delete announcements', 'error');
      return;
    }
    try {
      const result = await adminService.deleteAnnouncement(announcementId);
      if (result.success) {
        addNotification('Announcement deleted', 'success');
        setAnnouncements(announcements.filter(a => a.id !== announcementId));
      }
    } catch (error) {
      console.error('Delete announcement error:', error);
      addNotification('Failed to delete announcement', 'error');
    }
  };

  const toggleSelectBooking = (bookingId) => {
    if (selectedBookings.includes(bookingId)) {
      setSelectedBookings(selectedBookings.filter(id => id !== bookingId));
    } else {
      setSelectedBookings([...selectedBookings, bookingId]);
    }
  };

  const toggleSelectAll = () => {
    if (selectedBookings.length === filteredBookings.length) {
      setSelectedBookings([]);
    } else {
      setSelectedBookings(filteredBookings.map(b => b.id));
    }
  };

  const promoteFromWaitlist = async (bookingId) => {
    try {
      const result = await adminService.promoteFromWaitlist(bookingId);
      if (result.success) {
        addNotification('Booking promoted from waitlist', 'success');
        await loadBookings();
      }
    } catch (error) {
      console.error('Promote from waitlist error:', error);
      addNotification('Failed to promote booking', 'error');
    }
  };

  const removeFromWaitlist = async (bookingId) => {
    try {
      const result = await adminService.updateBookingStatus(bookingId, 'rejected', 'Removed from waitlist');
      if (result.success) {
        addNotification('Booking removed from waitlist', 'success');
        await loadBookings();
      }
    } catch (error) {
      console.error('Remove from waitlist error:', error);
      addNotification('Failed to remove booking', 'error');
    }
  };

  // ============================================
  // TABS CONFIGURATION
  // ============================================

  const getAvailableTabs = () => {
    const allTabs = [
      { id: 'dashboard', label: 'Dashboard', icon: 'fa-solid fa-chart-line', count: null, roles: ['admin', 'president', 'vicePresident', 'secretary', 'treasurer', 'clubManager', 'clubAdministrator'] },
      { id: 'bookings', label: 'Bookings', icon: 'fa-solid fa-calendar-check', count: pendingBookings.length, roles: ['admin', 'president', 'vicePresident', 'secretary', 'clubManager', 'clubAdministrator'] },
      { id: 'events', label: 'Events', icon: 'fa-solid fa-calendar-alt', count: null, roles: ['admin', 'president', 'vicePresident', 'secretary', 'sportsMember', 'culturalMember', 'technicalMember', 'socialMember'] },
      { id: 'reconciliation', label: 'Reconciliation', icon: 'fa-solid fa-rotate-left', count: null, roles: ['admin', 'president', 'treasurer'] },
      { id: 'sections', label: 'Sections', icon: 'fa-solid fa-people-group', count: pendingSections.length, roles: ['admin', 'president', 'vicePresident', 'secretary', 'sportsMember', 'culturalMember', 'technicalMember', 'socialMember', 'generalMember'] },
      { id: 'raffle', label: 'Raffle', icon: 'fa-solid fa-ticket', count: null, roles: ['admin', 'president', 'vicePresident'] },
      { id: 'associates', label: 'Associate Review', icon: 'fa-solid fa-user-check', count: null, roles: ['admin', 'president'] },
      { id: 'members', label: 'Members', icon: 'fa-solid fa-users', count: pendingMembers.length, roles: ['admin', 'president', 'vicePresident', 'secretary', 'clubAdministrator'] },
      { id: 'users', label: 'Users', icon: 'fa-solid fa-users-gear', count: null, roles: ['admin', 'president'] },
      { id: 'analytics', label: 'Analytics', icon: 'fa-solid fa-chart-line', count: null, roles: ['admin', 'president', 'vicePresident', 'secretary', 'treasurer'] },
      { id: 'settings', label: 'Settings', icon: 'fa-solid fa-cog', count: null, roles: ['admin', 'president', 'clubManager'] },
      { id: 'gallery', label: 'Gallery', icon: 'fa-solid fa-images', count: null, roles: ['admin', 'president'] }
    ];
    
    return allTabs.filter(tab => {
      if (isAdmin) return true;
      return tab.roles.includes(currentUserRole);
    });
  };
  
  const tabs = getAvailableTabs();
  
  const sectionSubTabs = [
    { id: 'pending', label: 'Pending', icon: 'fa-solid fa-clock', count: pendingSections.length, color: '#f59e0b' },
    { id: 'approved', label: 'Approved', icon: 'fa-solid fa-check-circle', count: approvedSections.length, color: '#10b981' },
    { id: 'rejected', label: 'Rejected', icon: 'fa-solid fa-times-circle', count: rejectedSections.length, color: '#ef4444' }
  ];
  
  const managementSubTabs = [
    { id: 'sections', label: 'Sections', icon: 'fa-solid fa-people-group', color: '#27a85a' },
    { id: 'facilities', label: 'Facilities', icon: 'fa-solid fa-building', color: '#3b82f6' },
    { id: 'equipment', label: 'Equipment', icon: 'fa-solid fa-tools', color: '#f59e0b' }
  ];

  const getQuickActionsData = () => {
    const actions = [];
    
    if (canViewAnalytics) {
      actions.push({ id: 'analytics', label: 'Analytics', icon: 'fa-solid fa-chart-line', color: '#00843D', action: () => setActiveTab('analytics') });
    }
    actions.push({ id: 'audit', label: 'Audit Log', icon: 'fa-solid fa-history', color: '#64748b', action: () => setShowAuditLogModal(true) });
    if (hasPermission(currentUserRole, 'generate_reports')) {
      actions.push({ id: 'reports', label: 'Reports', icon: 'fa-solid fa-file-alt', color: '#64748b', action: () => setShowReportModal(true) });
    }
    
    return actions;
  };

  const getManagementItemsData = () => {
    const items = [];
    
    items.push({ id: 'schedule', label: 'Schedule', icon: 'fa-solid fa-calendar-alt', color: '#00843D', action: () => setShowScheduleModal(true) });
    items.push({ id: 'conflicts', label: 'Conflicts', icon: 'fa-solid fa-exclamation-triangle', color: '#f59e0b', badge: conflicts.length, action: () => setShowConflictModal(true) });
    items.push({ id: 'waitlist', label: 'Waitlist', icon: 'fa-solid fa-hourglass-half', color: '#f59e0b', badge: waitlistBookings.length, action: () => setShowWaitlistModal(true) });
    if (hasPermission(currentUserRole, 'manage_maintenance') || isClubManager || isTechnicalMember) {
      items.push({ id: 'maintenance', label: 'Maintenance', icon: 'fa-solid fa-tools', color: '#64748b', action: () => setShowMaintenanceModal(true) });
    }
    
    return items;
  };

  const getSettingsItemsData = () => {
    const items = [];
    
    if (canManageBlackout) {
      items.push({ id: 'blackout', label: 'Blackout', icon: 'fa-solid fa-calendar-times', color: '#64748b', action: () => setShowBlackoutModal(true) });
    }
    if (canManagePricing) {
      items.push({ id: 'pricing', label: 'Pricing', icon: 'fa-solid fa-tag', color: '#64748b', action: () => setShowPricingModal(true) });
    }
    if (canAssignRoles) {
      items.push({ id: 'permissions', label: 'Permissions', icon: 'fa-solid fa-lock', color: '#64748b', action: () => setShowPermissionsModal(true) });
    }
    if (canManageAnnouncements) {
      items.push({ id: 'announce', label: 'Announce', icon: 'fa-solid fa-bullhorn', color: '#00843D', action: () => setShowAnnouncementModal(true) });
    }
    
    items.push({ id: 'management', label: 'Management', icon: 'fa-solid fa-database', color: '#8b5cf6', action: () => setActiveTab('management') });
    
    return items;
  };

  const getCommunicationItemsData = () => {
    const items = [];
    
    items.push({ id: 'feedback', label: 'Feedback', icon: 'fa-solid fa-star', color: '#f59e0b', badge: feedbacks.length, action: () => setShowFeedbackModal(true) });
    if (hasPermission(currentUserRole, 'send_communications')) {
      items.push({ id: 'messages', label: 'Messages', icon: 'fa-solid fa-message', color: '#00843D', action: () => setShowCommunicationModal(true) });
    }
    
    return items;
  };

  const quickActionsData = getQuickActionsData();
  const managementItemsData = getManagementItemsData();
  const settingsItemsData = getSettingsItemsData();
  const communicationItemsData = getCommunicationItemsData();

  // ============================================
  // RENDER CONTENT
  // ============================================

  const renderContent = () => {
    const props = {
      user,
      addNotification,
      addAuditLog,
      generateBookingId: () => `RA-${Date.now()}`,
      equipmentByCategory: {},
      sections: allSections,
      sectionMembers: {},
      equipmentCatalog: [],
      facilities: [],
      pendingBookings,
      pendingMembers,
      waitlistBookings,
      filteredBookings,
      stats,
      analytics,
      showAnalytics,
      setShowAnalytics,
      analyticsPeriod,
      setAnalyticsPeriod,
      filterStatus,
      setFilterStatus,
      filterType,
      setFilterType,
      filterFacility,
      setFilterFacility,
      searchTerm,
      setSearchTerm,
      dateRange,
      setDateRange,
      sortBy,
      setSortBy,
      selectedBookings,
      setSelectedBookings,
      toggleSelectBooking,
      toggleSelectAll,
      addToWaitlist: () => {},
      removeFromWaitlist,
      promoteFromWaitlist,
      delegatedUsers,
      availableUsers: [],
      handleDelegate,
      auditLog,
      communicationHistory,
      sendSMS: () => {},
      sendWhatsApp: () => {},
      sendEmail: () => {},
      locationChangeHistory,
      handleLocationChange,
      conflicts,
      detectConflicts,
      resolveConflict,
      feedbacks,
      submitFeedback: () => {},
      respondToFeedback,
      maintenanceSchedule,
      scheduleMaintenance,
      cancelMaintenance,
      blackoutDates,
      addBlackoutDate,
      removeBlackoutDate,
      pricingRules,
      addPricingRule,
      updatePricingRule,
      deletePricingRule,
      permissions,
      updateRolePermissions,
      announcements,
      createAnnouncement,
      deleteAnnouncement,
      myBookings: [],
      isAdmin,
      isPresident,
      isVicePresident,
      isSecretary,
      isTreasurer,
      presidentDelegation,
      canApprove,
      canManageUsers,
      canAssignRoles,
      canViewAnalytics,
      canManageSections,
      canManageEvents,
      canManageRaffle,
      canManageBlackout,
      canManagePricing,
      canManageAnnouncements,
      canApproveAssociates,
      canManageGallery
    };
    
    switch(activeTab) {
      case 'dashboard':
        return <AdminDashboard user={user} addNotification={addNotification} />;
      case 'associates':
        if (!canApproveAssociates) return <AccessDenied message="You do not have permission to review associate members" />;
        return <AdminReviewPanel user={user} addNotification={addNotification} />;
      case 'bookings':
        if (!hasPermission(currentUserRole, 'view_bookings') && !isAdmin && !isPresident && !isVicePresident && !isSecretary && !isClubManager && !isClubAdministrator) {
          return <AccessDenied message="You do not have permission to view bookings" />;
        }
        
        return (
          <>
            <div className="bookings-header">
              <AdminFilters
                filterStatus={filterStatus}
                setFilterStatus={setFilterStatus}
                filterType={filterType}
                setFilterType={setFilterType}
                filterFacility={filterFacility}
                setFilterFacility={setFilterFacility}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                dateRange={dateRange}
                setDateRange={setDateRange}
                sortBy={sortBy}
                setSortBy={setSortBy}
                facilities={[]}
                filteredCount={filteredBookings?.length || 0}
                selectedCount={selectedBookings?.length || 0}
                toggleSelectAll={toggleSelectAll}
                totalCount={filteredBookings?.length || 0}
              />
              <div className="export-actions">
                <button onClick={exportBookingsToCSV} className="btn-export">
                  <i className="fa-solid fa-download"></i> Export CSV
                </button>
                <button onClick={generateBookingReport} className="btn-report">
                  <i className="fa-solid fa-chart-line"></i> Generate Report
                </button>
                <button onClick={resetFilters} className="btn-reset">
                  <i className="fa-solid fa-undo"></i> Reset Filters
                </button>
              </div>
            </div>
            
            <div className="admin-section">
              <div className="section-header">
                <h3 className="section-title">
                  <i className="fa-solid fa-calendar-check"></i>
                  Bookings Management
                </h3>
                {selectedBookings.length > 0 && canApprove && (
                  <div className="bulk-actions">
                    <button onClick={handleBulkApprove} className="btn-bulk-approve">
                      <i className="fa-solid fa-check"></i> Approve ({selectedBookings.length})
                    </button>
                    <button onClick={handleBulkReject} className="btn-bulk-reject">
                      <i className="fa-solid fa-times"></i> Reject ({selectedBookings.length})
                    </button>
                  </div>
                )}
              </div>
              
              <AdminBookingList
                bookings={filteredBookings}
                selectedBookings={selectedBookings}
                toggleSelectBooking={toggleSelectBooking}
                canApprove={canApprove}
                isVicePresident={isVicePresident}
                presidentDelegation={presidentDelegation}
                setSelectedBooking={setSelectedBooking}
                setShowRejectionModal={setShowRejectionModal}
                setShowChangeLocationModal={setShowChangeLocationModal}
                setShowViewDetailsModal={setShowViewDetailsModal}
                setAvailableLocations={setAvailableLocations}
                facilities={[]}
                handleAdminAction={handleAdminAction}
                handleApproveBooking={handleApproveBooking}
                handleRejectBooking={openRejectionModal}
                handleAddToWaitlist={handleAddToWaitlist}
                handleViewDetails={handleViewBookingDetails}
                addToWaitlist={() => {}}
                showAllStatus={true}
              />
            </div>
          </>
        );
      case 'events':
        if (!canManageEvents && !hasPermission(currentUserRole, 'manage_events')) {
          return <AccessDenied message="You do not have permission to manage events" />;
        }
        return <AdminEvents user={user} addNotification={addNotification} />;
      case 'gallery':
        if (!canManageGallery) {
          return <AccessDenied message="You do not have permission to manage gallery" />;
        }
        return <AdminGallery addNotification={addNotification} />;
      case 'members':
        if (!canManageUsers) {
          return <AccessDenied message="You do not have permission to manage members" />;
        }
        return (
          <>
            {pendingMembers?.length > 0 && (
              <div className="admin-section">
                <h3 className="section-title">
                  <i className="fa-solid fa-user-plus"></i>
                  Pending Membership Requests ({pendingMembers.length})
                </h3>
                <div className="members-list">
                  {pendingMembers.map(member => (
                    <div key={member.id} className="member-item">
                      <div className="member-avatar">
                        <i className="fa-solid fa-user"></i>
                      </div>
                      <div className="member-info">
                        <h4>{member.first_name} {member.last_name}</h4>
                        <p><i className="fa-solid fa-envelope"></i> {member.email}</p>
                        <p><i className="fa-solid fa-phone"></i> {member.phone || 'Not provided'}</p>
                      </div>
                      <div className="member-actions">
                        <button className="action-btn approve" onClick={() => handleMemberAction(member.id, null, 'approved')}>
                          <i className="fa-solid fa-check"></i> Approve
                        </button>
                        <button className="action-btn reject" onClick={() => {
                          const reason = prompt('Enter rejection reason:');
                          if (reason) handleMemberAction(member.id, null, 'rejected', reason);
                        }}>
                          <i className="fa-solid fa-times"></i> Reject
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="admin-section">
              <h3 className="section-title">
                <i className="fa-solid fa-users"></i>
                Active Members
              </h3>
              <p className="coming-soon">Member management features coming soon...</p>
            </div>
          </>
        );
      case 'users':
        if (!canManageUsers && !canAssignRoles) {
          return <AccessDenied message="You do not have permission to manage users" />;
        }
        return (
          <UserManagement 
            users={allUsers}
            onUpdateUser={(userId) => {
              addNotification('User update feature coming soon', 'info');
            }}
            onDeleteUser={async (userId) => {
              if (!canManageUsers) {
                addNotification('You do not have permission to delete users', 'error');
                return;
              }
              if (window.confirm('Are you sure you want to delete this user?')) {
                const result = await adminService.deleteUser(userId);
                if (result.success) {
                  addNotification('User deleted successfully', 'success');
                  loadAllUsers();
                } else {
                  addNotification(result.message || 'Failed to delete user', 'error');
                }
              }
            }}
            onActivateUser={async (userId) => {
              if (!canManageUsers) {
                addNotification('You do not have permission to activate users', 'error');
                return;
              }
              const result = await adminService.activateUser(userId);
              if (result.success) {
                addNotification('User activated successfully', 'success');
                loadAllUsers();
              } else {
                addNotification(result.message || 'Failed to activate user', 'error');
              }
            }}
            onSuspendUser={async (userId, reason) => {
              if (!canManageUsers) {
                addNotification('You do not have permission to suspend users', 'error');
                return;
              }
              const result = await adminService.suspendUser(userId, reason);
              if (result.success) {
                addNotification('User suspended successfully', 'success');
                loadAllUsers();
              } else {
                addNotification(result.message || 'Failed to suspend user', 'error');
              }
            }}
  onAssignRole={async (userId, role) => {
  console.log('=== onAssignRole called ===');
  console.log('userId:', userId, 'role:', role);
  
  if (!canAssignRoles) {
    addNotification('You do not have permission to assign roles', 'error');
    return;
  }
  
  try {
    // FIRST: Get the current user data (this is what was missing!)
    const userResponse = await adminService.getUserById(userId);
    console.log('User response:', userResponse);
    
    if (!userResponse.success || !userResponse.user) {
      addNotification('Failed to get user data', 'error');
      return;
    }
    
    const user = userResponse.user;
    const reason = prompt('Enter reason for role change (optional):');
    
    // SECOND: Update with ALL required fields
    const result = await adminService.updateUser(userId, { 
      account_type: role,
      first_name: user.first_name,
      last_name: user.last_name,
      email: user.email,
      phone: user.phone || ''
    });
    
    if (result.success) {
      addNotification(`Role assigned to ${user.first_name} ${user.last_name} successfully!`, 'success');
      await loadAllUsers(); // Refresh the users list
    } else {
      addNotification(result.message || 'Failed to assign role', 'error');
    }
  } catch (error) {
    console.error('Role assignment error:', error);
    addNotification('Error assigning role: ' + error.message, 'error');
  }
}}
            addNotification={addNotification}
            currentUserRole={currentUserRole}
            canAssignRoles={canAssignRoles}
          />
        );
      case 'raffle':
        if (!canManageRaffle) {
          return <AccessDenied message="You do not have permission to manage raffle" />;
        }
        return <AdminRaffle user={user} addNotification={addNotification} />;
      case 'sections':
        if (!canManageSections && !hasPermission(currentUserRole, 'view_all_sections')) {
          return <AccessDenied message="You do not have permission to manage sections" />;
        }
        return (
          <motion.div key={sectionVersion} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }} className="tab-panel">
            <div className="section-header-actions">
              <h3 className="section-title"><i className="fa-solid fa-people-group"></i> Section Management</h3>
              <button className="refresh-btn" onClick={async () => { await refreshSectionsData(); addNotification('Sections data refreshed', 'success'); }}>
                <i className="fa-solid fa-rotate-right"></i> Refresh
              </button>
            </div>
            {allSections.length > 0 && (
              <div className="admin-section">
                <h3 className="section-title"><i className="fa-solid fa-chart-simple"></i> Section Statistics</h3>
                <div className="sections-stats-grid">
                  <div className="stat-card-small"><div className="stat-value">{allSections.length}</div><div className="stat-label">Total Sections</div></div>
                  <div className="stat-card-small"><div className="stat-value">{allSections.reduce((sum, s) => sum + (s.approved_count || 0), 0)}</div><div className="stat-label">Total Members</div></div>
                  <div className="stat-card-small"><div className="stat-value">{pendingSections.length}</div><div className="stat-label">Pending Requests</div></div>
                  <div className="stat-card-small"><div className="stat-value">{approvedSections.length}</div><div className="stat-label">Approved</div></div>
                  <div className="stat-card-small"><div className="stat-value">{rejectedSections.length}</div><div className="stat-label">Rejected</div></div>
                  <div className="stat-card-small"><div className="stat-value">{allSections.reduce((sum, s) => sum + (s.capacity || 0), 0)}</div><div className="stat-label">Total Capacity</div></div>
                </div>
              </div>
            )}
            <div className="section-subtabs">
              {sectionSubTabs.map(subtab => (
                <button key={subtab.id} className={`section-subtab-btn ${sectionSubTab === subtab.id ? 'active' : ''}`} onClick={() => setSectionSubTab(subtab.id)} style={{ borderBottomColor: sectionSubTab === subtab.id ? subtab.color : 'transparent', color: sectionSubTab === subtab.id ? subtab.color : '#666' }}>
                  <i className={subtab.icon}></i><span>{subtab.label}</span>
                  {subtab.count > 0 && <span className="subtab-badge" style={{ backgroundColor: subtab.color }}>{subtab.count}</span>}
                </button>
              ))}
            </div>
            {sectionSubTab === 'pending' && canManageSections && (
              <div className="admin-section">
                <h3 className="section-title"><i className="fa-solid fa-clock"></i> Pending Section Requests ({pendingSections.length})</h3>
                {pendingSections.length > 0 ? (
                  <div className="sections-list">
                    {pendingSections.map(request => {
                      const targetSection = allSections.find(s => s.name === request.section_name);
                      const isFull = targetSection && (targetSection.approved_count || 0) >= (targetSection.capacity || Infinity);
                      return (
                        <div key={request.id} className="section-request-item">
                          <div className="section-request-header">
                            <div className="section-icon"><i className="fa-solid fa-people-group"></i></div>
                            <div className="section-info">
                              <h4>{request.section_name}</h4>
                              <p className="section-category">{request.category}</p>
                              <span className="status-badge pending"><i className="fa-solid fa-clock"></i> Pending</span>
                              {isFull && <span className="full-badge"><i className="fa-solid fa-ban"></i> Section Full ({targetSection?.approved_count}/{targetSection?.capacity})</span>}
                            </div>
                          </div>
                          <div className="member-info-section">
                            <h5>Requested by:</h5>
                            <p><strong>{request.first_name} {request.last_name}</strong></p>
                            <p><i className="fa-solid fa-envelope"></i> {request.email}</p>
                            <p><i className="fa-solid fa-phone"></i> {request.phone || 'Not provided'}</p>
                            <p><i className="fa-solid fa-id-card"></i> Member ID: {request.membership_number}</p>
                            <p><i className="fa-regular fa-calendar"></i> Requested: {new Date(request.created_at).toLocaleDateString()}</p>
                          </div>
                          <div className="section-request-actions">
                            <button className="action-btn approve" onClick={() => handleSectionRequestAction(request.id, 'approved')} disabled={isFull}><i className="fa-solid fa-check"></i> Approve</button>
                            <button className="action-btn reject" onClick={() => { const reason = prompt('Enter rejection reason (will be sent via email):'); if (reason) handleSectionRequestAction(request.id, 'rejected', reason); }}><i className="fa-solid fa-times"></i> Reject</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="empty-state"><i className="fa-solid fa-check-circle"></i><p>No pending section requests at this time.</p></div>
                )}
              </div>
            )}
            <div className="admin-section">
              <h3 className="section-title"><i className="fa-solid fa-people-group"></i> All Sections ({allSections.length})</h3>
              <div className="sections-grid">
                {allSections.map(section => {
                  const enrolledCount = section.approved_count || 0;
                  const capacity = section.capacity || 0;
                  const percentage = capacity > 0 ? (enrolledCount / capacity) * 100 : 0;
                  return (
                    <div key={section.id} className="section-stat-card">
                      <div className="section-stat-header">
                        <div className="section-stat-icon"><i className={section.icon || "fa-solid fa-people-group"}></i></div>
                        <div className="section-stat-info"><h4>{section.name}</h4><span className="section-category">{section.category}</span></div>
                      </div>
                      <div className="section-stat-body">
                        <div className="stat-row"><span className="stat-label">Enrolled:</span><span className="stat-value">{enrolledCount} / {capacity || '∞'}</span></div>
                        <div className="stat-row"><span className="stat-label">Pending:</span><span className="stat-value pending">{section.pending_count || 0}</span></div>
                        <div className="progress-bar"><div className="progress-fill" style={{ width: `${Math.min(percentage, 100)}%` }}></div></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        );
      case 'reconciliation':
        if (!hasPermission(currentUserRole, 'view_financial_data') && !isAdmin && !isPresident && !isTreasurer) {
          return <AccessDenied message="You do not have permission to access reconciliation" />;
        }
        return <AdminReconciliation addNotification={addNotification} />;
      case 'analytics':
        if (!canViewAnalytics) {
          return <AccessDenied message="You do not have permission to view analytics" />;
        }
        return <AdminAnalytics analytics={analytics} analyticsPeriod={analyticsPeriod} setAnalyticsPeriod={setAnalyticsPeriod} communicationHistory={communicationHistory || []} fullView={true} />;
      case 'settings':
        if (!hasPermission(currentUserRole, 'manage_settings') && !isAdmin && !isPresident && !isClubManager) {
          return <AccessDenied message="You do not have permission to access settings" />;
        }
        return (
          <>
            {quickActionsData.length > 0 && (
              <div className="admin-section">
                <h3 className="section-title">
                  <i className="fa-solid fa-bolt"></i>
                  Quick Actions
                </h3>
                <div className="actions-grid">
                  {quickActionsData.map(action => (
                    <button key={action.id} className="action-card" onClick={action.action}>
                      <i className={action.icon} style={{ color: action.color }}></i>
                      <span>{action.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {managementItemsData.length > 0 && (
              <div className="admin-section">
                <h3 className="section-title">
                  <i className="fa-solid fa-chart-line"></i>
                  Management
                </h3>
                <div className="actions-grid">
                  {managementItemsData.map(item => (
                    <button key={item.id} className="action-card" onClick={item.action}>
                      <i className={item.icon} style={{ color: item.color }}></i>
                      <span>{item.label}</span>
                      {item.badge > 0 && <span className="action-badge">{item.badge}</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {settingsItemsData.length > 0 && (
              <div className="admin-section">
                <h3 className="section-title">
                  <i className="fa-solid fa-sliders-h"></i>
                  Settings
                </h3>
                <div className="actions-grid">
                  {settingsItemsData.map(item => (
                    <button key={item.id} className="action-card" onClick={item.action}>
                      <i className={item.icon} style={{ color: item.color }}></i>
                      <span>{item.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {communicationItemsData.length > 0 && (
              <div className="admin-section">
                <h3 className="section-title">
                  <i className="fa-solid fa-message"></i>
                  Communication
                </h3>
                <div className="actions-grid">
                  {communicationItemsData.map(item => (
                    <button key={item.id} className="action-card" onClick={item.action}>
                      <i className={item.icon} style={{ color: item.color }}></i>
                      <span>{item.label}</span>
                      {item.badge > 0 && <span className="action-badge">{item.badge}</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="settings-grid">
              <div className="setting-card"><i className="fa-solid fa-tools"></i><h4>Maintenance</h4><p>Schedule and manage facility maintenance</p><button className="setting-btn" onClick={() => setShowMaintenanceModal(true)}>Manage</button></div>
              <div className="setting-card"><i className="fa-solid fa-calendar-times"></i><h4>Blackout Dates</h4><p>Block dates for special events or closures</p><button className="setting-btn" onClick={() => setShowBlackoutModal(true)}>Manage</button></div>
              <div className="setting-card"><i className="fa-solid fa-tag"></i><h4>Pricing Rules</h4><p>Configure pricing for facilities and equipment</p><button className="setting-btn" onClick={() => setShowPricingModal(true)}>Manage</button></div>
              {canAssignRoles && (
                <div className="setting-card"><i className="fa-solid fa-lock"></i><h4>Permissions</h4><p>Manage role-based permissions</p><button className="setting-btn" onClick={() => setShowPermissionsModal(true)}>Manage</button></div>
              )}
              <div className="setting-card"><i className="fa-solid fa-bullhorn"></i><h4>Announcements</h4><p>Create and manage announcements</p><button className="setting-btn" onClick={() => setShowAnnouncementModal(true)}>Manage</button></div>
              <div className="setting-card"><i className="fa-solid fa-history"></i><h4>Audit Log</h4><p>View system activity history</p><button className="setting-btn" onClick={() => setShowAuditLogModal(true)}>View</button></div>
            </div>
          </>
        );
      case 'management':
        if (!isAdmin && !isPresident && !canManageSections && !canManageUsers) {
          return <AccessDenied message="You do not have permission to manage resources" />;
        }
        return (
          <div className="management-container">
            <div className="management-header">
              <button className="back-to-settings" onClick={() => setActiveTab('settings')}>
                <i className="fa-solid fa-arrow-left"></i> Back to Settings
              </button>
              <h2>Resource Management</h2>
              <p>Manage sections, facilities, and equipment in your database</p>
            </div>

            <div className="management-subtabs">
              {managementSubTabs.map(subtab => (
                <button
                  key={subtab.id}
                  className={`management-subtab-btn ${managementSubTab === subtab.id ? 'active' : ''}`}
                  onClick={() => setManagementSubTab(subtab.id)}
                  style={{ borderBottomColor: managementSubTab === subtab.id ? subtab.color : 'transparent' }}
                >
                  <i className={subtab.icon}></i>
                  <span>{subtab.label}</span>
                </button>
              ))}
            </div>

            {managementSubTab === 'sections' && (
              <div className="admin-section">
                <div className="section-header">
                  <h3><i className="fa-solid fa-people-group"></i> Sections Management</h3>
                  <button className="btn-primary-sm" onClick={() => openResourceModal('section', null)}>
                    <i className="fa-solid fa-plus"></i> Add Section
                  </button>
                </div>
                <div className="table-responsive">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Category</th>
                        <th>Instructor</th>
                        <th>Capacity</th>
                        <th>Enrolled</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allSections.map(section => (
                        <tr key={section.id}>
                          <td>{section.id}</td>
                          <td><i className={section.icon || "fa-solid fa-people-group"}></i> {section.name}</td>
                          <td>{section.category}</td>
                          <td>{section.instructor || '-'}</td>
                          <td>{section.capacity || '∞'}</td>
                          <td>{section.approved_count || 0}</td>
                          <td>
                            <button className="btn-edit-sm" onClick={() => openResourceModal('section', section)}>
                              <i className="fa-solid fa-edit"></i>
                            </button>
                            <button className="btn-delete-sm" onClick={() => deleteResource('section', section.id, section.name)}>
                              <i className="fa-solid fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {managementSubTab === 'facilities' && (
              <div className="admin-section">
                <div className="section-header">
                  <h3><i className="fa-solid fa-building"></i> Facilities Management</h3>
                  <button className="btn-primary-sm" onClick={() => openResourceModal('facility', null)}>
                    <i className="fa-solid fa-plus"></i> Add Facility
                  </button>
                </div>
                <div className="table-responsive">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Category</th>
                        <th>Location</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {facilitiesList.map(facility => (
                        <tr key={facility.id}>
                          <td>{facility.id}</td>
                          <td><i className={facility.icon || "fa-solid fa-building"}></i> {facility.name}</td>
                          <td>{facility.category}</td>
                          <td>{facility.location || '-'}</td>
                          <td>
                            <span className={`status-badge ${facility.maintenance_status === 'operational' ? 'active' : facility.maintenance_status === 'maintenance' ? 'warning' : 'danger'}`}>
                              {facility.maintenance_status || 'operational'}
                            </span>
                          </td>
                          <td>
                            <button className="btn-edit-sm" onClick={() => openResourceModal('facility', facility)}>
                              <i className="fa-solid fa-edit"></i>
                            </button>
                            <button className="btn-delete-sm" onClick={() => deleteResource('facility', facility.id, facility.name)}>
                              <i className="fa-solid fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {managementSubTab === 'equipment' && (
              <div className="admin-section">
                <div className="section-header">
                  <h3><i className="fa-solid fa-tools"></i> Equipment Management</h3>
                  <button className="btn-primary-sm" onClick={() => openResourceModal('equipment', null)}>
                    <i className="fa-solid fa-plus"></i> Add Equipment
                  </button>
                </div>
                <div className="table-responsive">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Name</th>
                        <th>Category</th>
                        <th>Available</th>
                        <th>Type</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {equipmentList.map(item => (
                        <tr key={item.id}>
                          <td>{item.id}</td>
                          <td><i className={item.icon || "fa-solid fa-box"}></i> {item.name}</td>
                          <td>{item.category}</td>
                          <td>{item.available}</td>
                          <td>{item.sporting_only ? 'Sporting Only' : 'General Use'}</td>
                          <td>
                            <button className="btn-edit-sm" onClick={() => openResourceModal('equipment', item)}>
                              <i className="fa-solid fa-edit"></i>
                            </button>
                            <button className="btn-delete-sm" onClick={() => deleteResource('equipment', item.id, item.name)}>
                              <i className="fa-solid fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      default:
        return <AdminDashboard user={user} addNotification={addNotification} />;
    }
  };
  
  if (loading && !initialLoadComplete) {
    return (
      <div className="admin-page loading">
        <div className="spinner"></div>
        <p>Loading admin dashboard...</p>
      </div>
    );
  }
  
  return (
    <div className="admin-page">
      <div className="toast-container"><AnimatePresence /></div>
      <div className="page-header">
        <h2 className="page-title">
          <i className="fa-solid fa-shield"></i> Admin Dashboard
          {isPresident && <span className="admin-badge president">President</span>}
          {isVicePresident && <span className="admin-badge vp">Vice President</span>}
          {isSecretary && <span className="admin-badge secretary">Secretary</span>}
          {isTreasurer && <span className="admin-badge treasurer">Treasurer</span>}
          {isSportsMember && <span className="admin-badge sports">Sports Member</span>}
          {isCulturalMember && <span className="admin-badge cultural">Cultural Member</span>}
          {isTechnicalMember && <span className="admin-badge technical">Technical Member</span>}
          {isSocialMember && <span className="admin-badge social">Social Member</span>}
          {isGeneralMember && <span className="admin-badge general">General Member</span>}
          {isClubManager && <span className="admin-badge manager">Club Manager</span>}
          {isClubAdministrator && <span className="admin-badge admin-staff">Club Admin</span>}
          {isAdmin && !isPresident && !isVicePresident && !isSecretary && !isTreasurer && !isSportsMember && !isCulturalMember && !isTechnicalMember && !isSocialMember && !isGeneralMember && !isClubManager && !isClubAdministrator && <span className="admin-badge">Admin</span>}
        </h2>
        <p className="page-description">Welcome back, {user?.first_name || 'Admin'}! Manage bookings, memberships, and system settings</p>
      </div>
      
      <div className="tab-navigation">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <i className={tab.icon}></i>
            <span>{tab.label}</span>
            {tab.count > 0 && <span className="tab-badge">{tab.count}</span>}
          </button>
        ))}
      </div>
      
      <div className="tab-content">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="tab-panel"
          >
            {renderContent()}
          </motion.div>
        </AnimatePresence>
      </div>
      
      <AdminModals
        showRejectionModal={showRejectionModal} setShowRejectionModal={setShowRejectionModal}
        showChangeLocationModal={showChangeLocationModal} setShowChangeLocationModal={setShowChangeLocationModal}
        showBulkActionModal={showBulkActionModal} setShowBulkActionModal={setShowBulkActionModal}
        showDelegateModal={showDelegateModal} setShowDelegateModal={setShowDelegateModal}
        showReportModal={showReportModal} setShowReportModal={setShowReportModal}
        showAuditLogModal={showAuditLogModal} setShowAuditLogModal={setShowAuditLogModal}
        showSMSModal={showSMSModal} setShowSMSModal={setShowSMSModal}
        showWhatsAppModal={showWhatsAppModal} setShowWhatsAppModal={setShowWhatsAppModal}
        showEmailModal={showEmailModal} setShowEmailModal={setShowEmailModal}
        showScheduleModal={showScheduleModal} setShowScheduleModal={setShowScheduleModal}
        showConflictModal={showConflictModal} setShowConflictModal={setShowConflictModal}
        showWaitlistModal={showWaitlistModal} setShowWaitlistModal={setShowWaitlistModal}
        showFeedbackModal={showFeedbackModal} setShowFeedbackModal={setShowFeedbackModal}
        showMaintenanceModal={showMaintenanceModal} setShowMaintenanceModal={setShowMaintenanceModal}
        showBlackoutModal={showBlackoutModal} setShowBlackoutModal={setShowBlackoutModal}
        showPricingModal={showPricingModal} setShowPricingModal={setShowPricingModal}
        showPermissionsModal={showPermissionsModal} setShowPermissionsModal={setShowPermissionsModal}
        showAnnouncementModal={showAnnouncementModal} setShowAnnouncementModal={setShowAnnouncementModal}
        selectedBooking={selectedBooking} selectedMember={selectedMember}
        selectedBookings={selectedBookings} filteredBookings={filteredBookings}
        facilities={[]} sections={[]} equipmentCatalog={[]} availableUsers={[]}
        auditLog={auditLog} communicationHistory={communicationHistory}
        locationChangeHistory={locationChangeHistory} conflicts={conflicts}
        feedbacks={feedbacks} maintenanceSchedule={maintenanceSchedule}
        blackoutDates={blackoutDates} pricingRules={pricingRules}
        permissions={permissions} announcements={announcements}
        rejectionReason={rejectionReason} setRejectionReason={setRejectionReason}
        newLocation={newLocation} setNewLocation={setNewLocation}
        locationChangeReason={locationChangeReason} setLocationChangeReason={setLocationChangeReason}
        availableLocations={availableLocations}
        smsMessage={smsMessage} setSmsMessage={setSmsMessage}
        whatsAppMessage={whatsAppMessage} setWhatsAppMessage={setWhatsAppMessage}
        emailSubject={emailSubject} setEmailSubject={setEmailSubject}
        emailBody={emailBody} setEmailBody={setEmailBody}
        maintenanceItem={maintenanceItem} setMaintenanceItem={setMaintenanceItem}
        blackoutItem={blackoutItem} setBlackoutItem={setBlackoutItem}
        pricingItem={pricingItem} setPricingItem={setPricingItem}
        selectedRole={selectedRole} setSelectedRole={setSelectedRole}
        rolePermissions={rolePermissions} setRolePermissions={setRolePermissions}
        announcementItem={announcementItem} setAnnouncementItem={setAnnouncementItem}
        handleAdminAction={handleAdminAction} handleMemberAction={handleMemberAction}
        handleBulkAction={handleBulkAction} handleBulkCommunication={handleBulkCommunication}
        handleDelegate={handleDelegate} handleLocationChangeSubmit={handleLocationChangeSubmit}
        resolveConflict={resolveConflict} respondToFeedback={respondToFeedback}
        scheduleMaintenance={scheduleMaintenance} cancelMaintenance={cancelMaintenance}
        addBlackoutDate={addBlackoutDate} removeBlackoutDate={removeBlackoutDate}
        addPricingRule={addPricingRule} updatePricingRule={updatePricingRule}
        deletePricingRule={deletePricingRule} updateRolePermissions={updateRolePermissions}
        createAnnouncement={createAnnouncement} deleteAnnouncement={deleteAnnouncement}
        promoteFromWaitlist={promoteFromWaitlist} removeFromWaitlist={removeFromWaitlist}
        user={user} canApprove={canApprove}
      />
      
      {showResourceModal && (
        <div className="modal-overlay" onClick={() => setShowResourceModal(false)}>
          <div className="resource-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingResource ? 'Edit' : 'Add'} {editingResource?.name !== undefined ? editingResource.location !== undefined ? 'Facility' : 'Section' : 'Equipment'}</h3>
              <button className="close-btn" onClick={() => setShowResourceModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              {Object.keys(resourceFormData).map(key => (
                <div className="form-group" key={key}>
                  <label>{key.replace(/_/g, ' ').toUpperCase()}</label>
                  {key === 'description' || key === 'bio' ? (
                    <textarea value={resourceFormData[key] || ''} onChange={e => setResourceFormData({...resourceFormData, [key]: e.target.value})} rows="3" />
                  ) : key === 'amenities' || key === 'images' || key === 'alternate_locations' ? (
                    <input type="text" value={JSON.stringify(resourceFormData[key] || [])} onChange={e => { try { setResourceFormData({...resourceFormData, [key]: JSON.parse(e.target.value)}); } catch(e) { console.log('Invalid JSON'); } }} placeholder='["item1", "item2"]' />
                  ) : (
                    <input type={typeof resourceFormData[key] === 'number' ? 'number' : 'text'} value={resourceFormData[key] || ''} onChange={e => setResourceFormData({...resourceFormData, [key]: e.target.value})} />
                  )}
                </div>
              ))}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowResourceModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={handleSaveResource} disabled={savingResource}>{savingResource ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
      
      {showViewDetailsModal && selectedBooking && (
        <div className="modal-overlay" onClick={() => setShowViewDetailsModal(false)}>
          <div className="view-details-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <i className="fa-solid fa-receipt"></i>
                Booking Details
              </h3>
              <button className="close-btn" onClick={() => setShowViewDetailsModal(false)}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>
            
            <div className="modal-body">
              <div className="booking-summary">
                <div className="summary-header">
                  <span className="booking-number">{selectedBooking.booking_number}</span>
                  <span className={`status-badge ${selectedBooking.status}`}>
                    {selectedBooking.status?.toUpperCase()}
                  </span>
                </div>
                
                <div className="details-grid">
                  <div className="detail-group">
                    <label>Member Information</label>
                    <p><strong>Name:</strong> {selectedBooking.member_full_name || `${selectedBooking.member_first_name} ${selectedBooking.member_last_name}`}</p>
                    <p><strong>Email:</strong> {selectedBooking.member_email || selectedBooking.email}</p>
                    <p><strong>Phone:</strong> {selectedBooking.member_phone || selectedBooking.phone}</p>
                    <p><strong>Membership #:</strong> {selectedBooking.member_membership_number || selectedBooking.membership_number}</p>
                  </div>
                  
                  <div className="detail-group">
                    <label>Booking Details</label>
                    <p><strong>Type:</strong> {selectedBooking.booking_type?.toUpperCase() || 'N/A'}</p>
                    <p><strong>Facility:</strong> {selectedBooking.facility_name || selectedBooking.location}</p>
                    <p><strong>Date:</strong> {selectedBooking.booking_date ? new Date(selectedBooking.booking_date).toLocaleDateString() : 'N/A'}</p>
                    <p><strong>Time:</strong> {selectedBooking.start_time && selectedBooking.end_time ? 
                      `${selectedBooking.start_time.substring(0,5)} - ${selectedBooking.end_time.substring(0,5)}` : 'N/A'}</p>
                    <p><strong>Event Type:</strong> {selectedBooking.event_type || 'Private'}</p>
                    <p><strong>Guests:</strong> {selectedBooking.guests || 1}</p>
                  </div>
                  
                  {(selectedBooking.contact_phone || selectedBooking.contact_whatsapp || selectedBooking.contact_email) && (
                    <div className="detail-group">
                      <label>Contact Details</label>
                      {selectedBooking.contact_phone && <p><strong>Phone:</strong> {selectedBooking.contact_phone}</p>}
                      {selectedBooking.contact_whatsapp && <p><strong>WhatsApp:</strong> {selectedBooking.contact_whatsapp}</p>}
                      {selectedBooking.contact_email && <p><strong>Email:</strong> {selectedBooking.contact_email}</p>}
                    </div>
                  )}
                  
                  {selectedBooking.notes && (
                    <div className="detail-group full-width">
                      <label>Notes / Special Requests</label>
                      <div className="notes-content">
                        <i className="fa-solid fa-quote-left"></i>
                        {selectedBooking.notes}
                      </div>
                    </div>
                  )}
                  
                  {selectedBooking.equipment_items && selectedBooking.equipment_items.length > 0 && (
                    <div className="detail-group full-width">
                      <label>Equipment Requested</label>
                      <div className="equipment-tags">
                        {selectedBooking.equipment_items.map((item, idx) => (
                          <span key={idx} className="equipment-tag">
                            {typeof item === 'string' ? item : item.name}
                            {item.quantity && ` (${item.quantity})`}
                            {item.size && ` - Size: ${item.size}`}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {(canApprove && selectedBooking.status !== 'approved' && selectedBooking.status !== 'rejected') && (
                    <div className="detail-group full-width location-change-section">
                      <label>Change Location</label>
                      <div className="location-change-controls">
                        <select 
                          value={newLocation} 
                          onChange={(e) => setNewLocation(e.target.value)}
                          className="location-select"
                        >
                          <option value="">Select new location...</option>
                          {availableLocations.map(loc => (
                            <option key={loc} value={loc}>{loc}</option>
                          ))}
                        </select>
                        <textarea
                          placeholder="Reason for location change..."
                          value={locationChangeReason}
                          onChange={(e) => setLocationChangeReason(e.target.value)}
                          className="reason-textarea"
                          rows="2"
                        />
                        <button 
                          className="btn-submit-location"
                          onClick={handleLocationChangeSubmit}
                          disabled={!newLocation || !locationChangeReason}
                        >
                          <i className="fa-solid fa-location-dot"></i> Apply Location Change
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {selectedBooking.booking_date && (
                    <div className="detail-group">
                      <label>Date Status</label>
                      <p>
                        {new Date(selectedBooking.booking_date) < new Date() && (
                          <span className="status-badge-date past">Past Event</span>
                        )}
                        {new Date(selectedBooking.booking_date).toDateString() === new Date().toDateString() && (
                          <span className="status-badge-date today">Today's Event</span>
                        )}
                        {new Date(selectedBooking.booking_date) > new Date() && (
                          <span className="status-badge-date upcoming">Upcoming Event</span>
                        )}
                      </p>
                    </div>
                  )}
                  
                  <div className="detail-group">
                    <label>Created At</label>
                    <p>{selectedBooking.created_at ? new Date(selectedBooking.created_at).toLocaleString() : 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="btn-close" onClick={() => setShowViewDetailsModal(false)}>
                Close
              </button>
              {canApprove && selectedBooking.status === 'pending' && (
                <>
                  <button 
                    className="btn-approve-modal"
                    onClick={() => {
                      handleApproveBooking(selectedBooking.id);
                      setShowViewDetailsModal(false);
                    }}
                  >
                    <i className="fa-solid fa-check"></i> Approve
                  </button>
                  <button 
                    className="btn-reject-modal"
                    onClick={() => {
                      setShowViewDetailsModal(false);
                      openRejectionModal(selectedBooking);
                    }}
                  >
                    <i className="fa-solid fa-times"></i> Reject
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Access Denied Component
const AccessDenied = ({ message }) => (
  <div className="access-denied">
    <i className="fa-solid fa-lock"></i>
    <h3>Access Denied</h3>
    <p>{message || "You don't have permission to access this section."}</p>
  </div>
);

export default AdminPage;